import { z } from "zod";
import { EmissionFactorScope } from "../emissionFactor";
import { EcoLabel, EnvironmentalCategory, EnvironmentalMetricType, EnvironmentalSubCategory, EnvironmentalTargetType, WasteDisposalDestination } from "../environmentalCommitment";
// ---------------------------------------------------------------------------
// Target Method
// ---------------------------------------------------------------------------
/**
 * How a target is measured. `ReportOnly` carries no threshold — it records
 * that a scope is tracked deliberately (and on whose authority) so that fact
 * reaches the package screen, the Confirm summary and analytics, none of
 * which can see the project's Category Set-up tree.
 */
export var EnvironmentalTargetMethod;
(function (EnvironmentalTargetMethod) {
    EnvironmentalTargetMethod["Percentage"] = "environmental-target-method_percentage";
    EnvironmentalTargetMethod["FixedValue"] = "environmental-target-method_fixed-value";
    EnvironmentalTargetMethod["ReportOnly"] = "environmental-target-method_report-only";
    /**
     * A change against the project's own starting point: `baseline_value` is the
     * reference figure for the whole project, `target_value` is the SIZE OF THE
     * CHANGE as a positive percentage of it, and `baseline_factor` says which
     * way — `≤ Baseline − x%` or `≥ Baseline + x%`.
     *
     * The threshold that gets scored is derived (`baselineTargetThreshold`), so
     * there is one arithmetic and the numbers on screen are the ones the user
     * actually knows. Offered on both measures; an emissions target is locked to
     * `≤`. Was `ReductionAgainstBaseline` (a cut only, emissions only) until
     * 2026-09-17. See `docs/analytics/environmental-targets.md`.
     */
    EnvironmentalTargetMethod["BaselineToTarget"] = "environmental-target-method_baseline-to-target";
})(EnvironmentalTargetMethod || (EnvironmentalTargetMethod = {}));
export const environmentalTargetMethodToString = (m) => {
    switch (m) {
        case EnvironmentalTargetMethod.Percentage:
            return "Target percentage";
        case EnvironmentalTargetMethod.FixedValue:
            return "Fixed value";
        case EnvironmentalTargetMethod.ReportOnly:
            return "Report only - no threshold";
        case EnvironmentalTargetMethod.BaselineToTarget:
            return "Baseline to target";
        default:
            return String(m);
    }
};
// ---------------------------------------------------------------------------
// Measured By
// ---------------------------------------------------------------------------
/**
 * What counts toward the target's numerator.
 *
 * Derived for every method but Percentage: a fixed value, baseline or
 * report-only target is always the scope's summed quantity.
 *
 * On a Percentage target it is an explicit CHOICE — how a row counts as
 * "good" — offered by {@link measuredByOptionsForTarget}:
 *
 * - `RecycledPercentage`: each row contributes `quantity × recycled %`.
 * - `DestinationShare` (Waste): a row counts in full when its disposal
 *   destination is one the target picked (`waste_disposal_destinations`).
 * - `SourceShare` (Water): a row counts in full when its category is one the
 *   target picked (`share_categories`); discharged water is outside the share.
 *
 * The choice is explicit on purpose: an earlier design switched to a share
 * whenever the picker had values, so clearing it silently changed what the
 * target measured. `computeEnvironmentalTargetAttainment` branches on it.
 *
 * Two options it once offered are gone for good: `ShareOfQuantities` on a
 * percentage measured against a scope one level wider than the target named,
 * and `WaterSourceRecycled` inferred water's recycled share from the
 * sub-category a row was filed under.
 */
export var EnvironmentalTargetMeasuredBy;
(function (EnvironmentalTargetMeasuredBy) {
    /** Fixed value and report-only: the scope's reported `quantity`, summed. */
    EnvironmentalTargetMeasuredBy["ShareOfQuantities"] = "environmental-target-measured-by_share-of-quantities";
    /** Percentage: weighted by the row's `recycledPct`. */
    EnvironmentalTargetMeasuredBy["RecycledPercentage"] = "environmental-target-measured-by_recycled-percentage";
    /** Percentage on Waste: share of tonnes sent to the picked destinations. */
    EnvironmentalTargetMeasuredBy["DestinationShare"] = "environmental-target-measured-by_destination-share";
    /** Percentage on Water: share of withdrawn water from the picked categories. */
    EnvironmentalTargetMeasuredBy["SourceShare"] = "environmental-target-measured-by_source-share";
    /**
     * Percentage on Materials: share of the scope carried by rows holding an
     * approved eco-label. Counts a row IN FULL, like the two shares above and
     * unlike `RecycledPercentage` — a product either holds a certification or it
     * does not.
     *
     * Only ever paired with `EnvironmentalTargetMeasure.Value`, so the share is
     * weighted by money. See {@link measuredByOptionsForTarget}.
     */
    EnvironmentalTargetMeasuredBy["EcoLabelShare"] = "environmental-target-measured-by_eco-label-share";
})(EnvironmentalTargetMeasuredBy || (EnvironmentalTargetMeasuredBy = {}));
export const environmentalTargetMeasuredByToString = (b) => {
    switch (b) {
        case EnvironmentalTargetMeasuredBy.ShareOfQuantities:
            return "Reported Quantity";
        case EnvironmentalTargetMeasuredBy.RecycledPercentage:
            return "Recycled Content";
        case EnvironmentalTargetMeasuredBy.DestinationShare:
            return "Destination share";
        case EnvironmentalTargetMeasuredBy.SourceShare:
            return "Source share";
        case EnvironmentalTargetMeasuredBy.EcoLabelShare:
            return "Eco-label share";
        default:
            return String(b);
    }
};
// ---------------------------------------------------------------------------
// Measure
// ---------------------------------------------------------------------------
/**
 * What the target is counted in.
 *
 * `NativeQuantity` is every target that existed before emissions targets: the
 * scope's own reported unit — litres, tonnes, kilowatt-hours — and it is the
 * only measure the unit-coherence rule applies to.
 *
 * `Emissions` counts tCO₂e instead. That is not a formatting choice: it changes
 * the numerator, and it is the one case where a target may span leaves that
 * report in different units, because the emission factor IS the common
 * denominator the rule exists for the absence of.
 */
export var EnvironmentalTargetMeasure;
(function (EnvironmentalTargetMeasure) {
    EnvironmentalTargetMeasure["NativeQuantity"] = "environmental-target-measure_native-quantity";
    EnvironmentalTargetMeasure["Emissions"] = "environmental-target-measure_emissions";
    /**
     * Dollars — the reported rows' `materialsValue`, ex-GST.
     *
     * The third answer to "what are the figures counted in", added for
     * ">9% of materials/products BY VALUE carry an eco-label": a share weighted
     * by money rather than by quantity, which every share before it was.
     *
     * Suspends the unit-coherence rule for the SAME stated reason `Emissions`
     * does — money is a common denominator across leaves that report in tonnes,
     * litres and kilograms, which is the thing the rule exists for the absence
     * of. Concrete and paint do not add up; their invoices do.
     *
     * Offered on Materials and on `Percentage` only (see
     * `measuredByOptionsForTarget`). Not on `FixedValue`, which would need a
     * chosen `measuredBy` on a method where it is derived, and not on
     * `BaselineToTarget`. Neither has been asked for, and both would cost that
     * invariant.
     */
    EnvironmentalTargetMeasure["Value"] = "environmental-target-measure_value";
})(EnvironmentalTargetMeasure || (EnvironmentalTargetMeasure = {}));
export const environmentalTargetMeasureToString = (m) => {
    switch (m) {
        case EnvironmentalTargetMeasure.NativeQuantity:
            return "Reported Quantity";
        case EnvironmentalTargetMeasure.Emissions:
            return "Emissions (tCO₂e)";
        case EnvironmentalTargetMeasure.Value:
            return "Value ($)";
        default:
            return String(m);
    }
};
// ---------------------------------------------------------------------------
// Direction
// ---------------------------------------------------------------------------
/**
 * Which way is good. Read it through {@link directionForTarget}, never directly.
 *
 * Stored in exactly one place: `baseline_factor` on a Baseline to target
 * target, where the user chooses it ("Baseline factor"). Everywhere else it is
 * derived, and there is no general `target_direction` column — see
 * `directionForTarget` for why.
 *
 * `DoNotExceed` is a ceiling, and its pace inverts — `capPace` rather than
 * `absolutePace`. Every path before emissions assumed `ReachOrExceed` and
 * clamped at 100%, which for a ceiling scores exactly backwards and saturates:
 * a project four times over its cap read On Track and could not be made to read
 * worse, while one 80% under read At Risk.
 */
export var EnvironmentalTargetDirection;
(function (EnvironmentalTargetDirection) {
    EnvironmentalTargetDirection["ReachOrExceed"] = "environmental-target-direction_reach-or-exceed";
    EnvironmentalTargetDirection["DoNotExceed"] = "environmental-target-direction_do-not-exceed";
})(EnvironmentalTargetDirection || (EnvironmentalTargetDirection = {}));
export const environmentalTargetDirectionToString = (d) => {
    switch (d) {
        case EnvironmentalTargetDirection.ReachOrExceed:
            return "Reach or exceed";
        case EnvironmentalTargetDirection.DoNotExceed:
            return "Do not exceed";
        default:
            return String(d);
    }
};
/** The "Baseline factor" select's wording for each direction. */
export const environmentalTargetBaselineFactorToString = (d) => {
    switch (d) {
        case EnvironmentalTargetDirection.ReachOrExceed:
            return "≥ (greater than or equal)";
        case EnvironmentalTargetDirection.DoNotExceed:
            return "≤ (less than or equal)";
        default:
            return String(d);
    }
};
// ---------------------------------------------------------------------------
// Zod schema for the environmental target entity
// ---------------------------------------------------------------------------
/**
 * A target is scoped by `(target_type, category?, sub_category?)` and resolves
 * to matching commitment leaves at query time — it deliberately holds no list
 * of commitment ids, so "all categories" stays expressible and newly enabled
 * leaves join an existing target's scope.
 */
export const projectEnvironmentalTargetSchema = z.object({
    /**
     * `BaselineToTarget` only, and required there: `DoNotExceed` is
     * `≤ Baseline − x%`, `ReachOrExceed` is `≥ Baseline + x%`. Always
     * `DoNotExceed` on an emissions target.
     */
    baseline_factor: z.enum(EnvironmentalTargetDirection).optional(),
    /**
     * A Baseline to target target's starting point: the project's reference
     * figure for the WHOLE project, in the target's unit — the tender or
     * reference-design estimate. Required by `BaselineToTarget` and meaningless
     * to every other method.
     *
     * Stored rather than the derived ceiling, because it is the number the user
     * knows and the one the reader needs: a percentage with no baseline beside it
     * says nothing, since the same physical cut reads as 5%, 8% or 13% depending
     * only on what it is measured from. The ceiling follows from it and the cut
     * (`environmentalTargetThreshold`), in one place.
     */
    baseline_value: z.number().optional(),
    category: z.enum(EnvironmentalCategory).optional(),
    created: z.date(),
    description: z.string(),
    /**
     * `EcoLabelShare` only: which eco-labels count toward the share. Absent or
     * empty means ANY approved label — the same "absent = every one" wildcard
     * `category`, `sub_category` and `emission_scopes` use, so the client's
     * plain "a green/eco-label" needs no sentinel.
     *
     * Like `waste_disposal_destinations` on a Destination share these define the
     * NUMERATOR, not a row filter: narrowing the rows here would drop unlabelled
     * materials from the denominator and read 100% by construction.
     */
    eco_labels: z.array(z.enum(EcoLabel)).optional(),
    /**
     * Which GHG scopes an emissions target covers. Absent means ALL of them —
     * the same wildcard convention `category` and `sub_category` already use, so
     * "our whole footprint" needs no sentinel value.
     *
     * Meaningless on a `NativeQuantity` target and not written for one.
     */
    emission_scopes: z.array(z.enum(EmissionFactorScope)).optional(),
    generation_source: z.custom().optional(),
    measure: z.enum(EnvironmentalTargetMeasure),
    measured_by: z.enum(EnvironmentalTargetMeasuredBy),
    /** Derived from the scope's category, not chosen; absent for ratio methods. */
    metric_type: z.enum(EnvironmentalMetricType).optional(),
    project_environmental_target_id: z.string(),
    project_id: z.string(),
    /**
     * `SourceShare` only: the water categories whose rows count toward the share.
     */
    share_categories: z.array(z.enum(EnvironmentalCategory)).optional(),
    source_framework_id: z.string().optional(),
    source_framework_target_id: z.string().optional(),
    sub_category: z.enum(EnvironmentalSubCategory).optional(),
    target_method: z.enum(EnvironmentalTargetMethod),
    /**
     * Absent means EVERY type — the same wildcard as `category`.
     *
     * Only an emissions target can leave it out, and it must be able to: "total
     * Scope 3" spans Waste, Materials and Transport, so it has no single type. A
     * native-quantity target without a type would be summing litres and tonnes,
     * which is the thing the coherence rule exists to prevent.
     */
    target_type: z.enum(EnvironmentalTargetType).optional(),
    /** Percentage (0-100) or an absolute quantity; 0 for report-only targets. */
    target_value: z.number(),
    /**
     * Destinations on a Reported Quantity Waste target. Absent or empty = all.
     * On `DestinationShare` they define which rows count toward the share; on
     * every other method they narrow which rows are counted at all.
     */
    waste_disposal_destinations: z
        .array(z.enum(WasteDisposalDestination))
        .optional(),
    /**
     * Reported Quantity Waste targets only: keep only hazardous (`true`) or only
     * non-hazardous (`false`) rows. Absent = Any. A row with no hazardous flag
     * matches Any only.
     */
    waste_hazardous: z.boolean().optional()
});
